import { useCallback, useEffect, useRef, useState } from "react";

import useVoiceOrder, { VOICE_STATE } from "./useVoiceOrder";
import { findClosestOption, matchSpoken } from "./voiceMatch";

const DONE_RE = /\b(done|that'?s all|that is all|finished|that's it)\b/;
const YES_RE = /\b(yes|yeah|yep|yup|sure|correct|right|ok|okay|confirm)\b/;
const NO_RE = /\b(no|nope|nah|wrong|cancel|never\s?mind)\b/;
const CONFIRM_PHRASES = ["yes", "no", "yeah", "nope", "correct", "wrong"];
const LOOP_DELAY_MS = 500;
const CONFIRM_WINDOW_MS = 2000;

const longest = (opts) =>
  opts.reduce((a, b) => ((b.label || "").length > (a.label || "").length ? b : a));

const labelOf = (o) => (o.label || "").toLowerCase();
const isDoneChip = (c) => c.value === "__done__" || c.action === "done";

const useVoiceCommands = ({
  busy,
  showInput,
  showItems,
  showChips,
  multi,
  items,
  chips,
  stepId,
  voiceMode,
  onEnableVoiceMode,
  handleChipClick,
  handleVoiceMultiSelect,
  onFillInput,
}) => {
  const [pendingMatch, setPendingMatch] = useState(null);
  const [heardMatch, setHeardMatch] = useState(null);
  const lastAddRef = useRef(null);

  const pool = (showItems ? items : showChips ? chips : []).filter(
    (o) => !isDoneChip(o),
  );
  const poolKey = pool.map((o) => o.value).join("|");
  const isMultiStep = showItems || (showChips && multi);
  const hasSelection = pool.some((o) => o.selected);

  const phraseList = pendingMatch
    ? [...CONFIRM_PHRASES, pendingMatch.option.label]
    : heardMatch
      ? [
          ...CONFIRM_PHRASES,
          ...(heardMatch.multi ? ["done"] : []),
          ...pool.map(labelOf),
        ]
      : showInput
        ? null
        : showItems
          ? [...items.map(labelOf), "done", ...CONFIRM_PHRASES]
          : showChips
            ? [
                ...chips.filter((c) => !isDoneChip(c)).map(labelOf),
                ...(multi ? ["done"] : []),
                ...CONFIRM_PHRASES,
              ]
            : null;

  useEffect(() => {
    setPendingMatch(null);
    setHeardMatch(null);
    lastAddRef.current = null;
  }, [poolKey]);

  const onVoiceResult = useCallback(
    (text, meta = {}) => {
      const lower = text.toLowerCase().trim();
      const silence = !lower && meta.hadSpeech === false;

      if (heardMatch) {
        const opt = heardMatch.option;
        const isMultiHeard = !!heardMatch.multi;

        if (lower && NO_RE.test(lower)) {
          setHeardMatch(null);
          lastAddRef.current = null;
          if (isMultiHeard) {
            pool.filter((o) => o.selected).forEach((o) => handleChipClick(o));
          }
          return;
        }

        if (lower && isMultiHeard && DONE_RE.test(lower)) {
          setHeardMatch(null);
          const already = pool.filter((o) => o.selected).map((o) => o.value);
          handleVoiceMultiSelect(already);
          return;
        }

        if (lower) {
          const alt = matchSpoken(lower, pool, labelOf);
          if (alt.length) {
            const best = longest(alt);
            if (best.value !== opt.value) {
              if (isMultiHeard) {
                if (!best.selected) {
                  lastAddRef.current = best.value;
                  handleChipClick(best);
                }
                setHeardMatch({ option: best, multi: true });
              } else {
                setHeardMatch({ option: best });
              }
              return;
            }
          }
        }

        setHeardMatch(null);
        if (isMultiHeard) {
          if (silence) {
            const already = pool.filter((o) => o.selected).map((o) => o.value);
            handleVoiceMultiSelect(already);
          }
        } else {
          handleChipClick(opt);
        }
        return;
      }

      if (!lower) {
        if (silence && voiceMode && isMultiStep && hasSelection) {
          const already = pool.filter((o) => o.selected).map((o) => o.value);
          handleVoiceMultiSelect(already);
        }
        return;
      }

      if (pendingMatch) {
        if (NO_RE.test(lower)) {
          setPendingMatch(null);
          return;
        }
        if (YES_RE.test(lower)) {
          const opt = pendingMatch.option;
          setPendingMatch(null);
          handleChipClick(opt);
          return;
        }
      }
      setPendingMatch(null);

      if (showInput) {
        onFillInput(text.trim());
        return;
      }

      if (isMultiStep) {
        const saysDone = DONE_RE.test(lower);
        const mentioned = matchSpoken(lower, pool, labelOf);

        if (mentioned.length === 0 && !saysDone && NO_RE.test(lower)) {
          const selected = pool.filter((o) => o.selected);
          if (selected.length) {
            lastAddRef.current = null;
            selected.forEach((o) => handleChipClick(o));
          }
          return;
        }

        if (mentioned.length === 0 && !saysDone) {
          const close = findClosestOption(lower, pool, labelOf);
          if (close) setPendingMatch({ option: close.option });
          return;
        }

        if (saysDone) {
          const already = pool.filter((o) => o.selected).map((o) => o.value);
          const values = [
            ...new Set([...already, ...mentioned.map((o) => o.value)]),
          ];
          handleVoiceMultiSelect(values);
          return;
        }

        let lastOpt = null;
        mentioned.forEach((o) => {
          if (!o.selected) {
            lastAddRef.current = o.value;
            handleChipClick(o);
            lastOpt = o;
          }
        });
        if (lastOpt) setHeardMatch({ option: lastOpt, multi: true });
        return;
      }

      if (showChips) {
        const matches = matchSpoken(lower, pool, labelOf);
        if (matches.length) {
          setHeardMatch({ option: longest(matches) });
          return;
        }
        const close = findClosestOption(lower, pool, labelOf);
        if (close) setPendingMatch({ option: close.option });
      }
    },
    [
      pendingMatch,
      heardMatch,
      showInput,
      showItems,
      showChips,
      multi,
      pool,
      isMultiStep,
      hasSelection,
      voiceMode,
      handleChipClick,
      handleVoiceMultiSelect,
      onFillInput,
    ],
  );

  const voice = useVoiceOrder(phraseList, onVoiceResult);
  const { voiceState, startRecording } = voice;

  useEffect(() => {
    if (!voiceMode) return undefined;
    if (busy || showInput) return undefined;
    if (!(showItems || showChips)) return undefined;
    if (voiceState !== VOICE_STATE.DONE && voiceState !== VOICE_STATE.IDLE) {
      return undefined;
    }
    const noSpeechMs = heardMatch
      ? CONFIRM_WINDOW_MS
      : isMultiStep && hasSelection
        ? 2500
        : undefined;
    const delay = heardMatch ? 100 : LOOP_DELAY_MS;
    const t = setTimeout(() => startRecording({ noSpeechMs }), delay);
    return () => clearTimeout(t);
  }, [voiceMode, busy, showInput, showItems, showChips, voiceState, stepId, isMultiStep, hasSelection, heardMatch, startRecording]);

  const startSession = useCallback(() => {
    onEnableVoiceMode?.();
    startRecording();
  }, [onEnableVoiceMode, startRecording]);

  const confirmMatch = useCallback(() => {
    if (pendingMatch) handleChipClick(pendingMatch.option);
    setPendingMatch(null);
  }, [pendingMatch, handleChipClick]);

  const dismissMatch = useCallback(() => setPendingMatch(null), []);

  return {
    ...voice,
    showMicButton: !busy && (showItems || showChips || showInput),
    isRecording: voiceState === VOICE_STATE.RECORDING,
    isProcessing: voiceState === VOICE_STATE.PROCESSING,
    inSession: !!voiceMode,
    startSession,
    heardValue: heardMatch?.option?.value ?? null,
    pendingMatch: pendingMatch ? { label: pendingMatch.option.label } : null,
    confirmMatch,
    dismissMatch,
  };
};

export default useVoiceCommands;
